import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    History,
    Plus,
    Search,
    Filter,
    Calendar,
    Trash2,
    ArrowLeft,
    Package,
    CheckCircle2,
    X,
    FileDown,
    ChevronDown,
    Printer,
    FileSpreadsheet,
    FileText
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import AdminCard from "../../../components/common/AdminCard";
import AdminInput from "../../../components/common/AdminInput";
import { CustomDropdown } from "../../../components/common/CustomDropdown";
import { fetchProducts } from '../../../redux/slices/productSlice';
import { fetchUsers } from '../../../redux/slices/userSlice';
import {
    fetchInventoryLogs,
    createInventoryRecord,
    removeInventoryRecord,
    clearInventoryMessages
} from '../../../redux/slices/inventorySlice';

const InventoryHistory = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { products } = useSelector((state) => state.product);
    const { users } = useSelector((state) => state.user);
    const { logs, loading, error, success } = useSelector((state) => state.inventory);

    const location = useLocation();
    const state = location.state || {};

    const [showAddModal, setShowAddModal] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [filters, setFilters] = useState({
        productId: state.productId || '',
        type: state.type || '',
        fromDate: state.fromDate || new Date().toISOString().substring(0, 7) + '-01',
        toDate: state.toDate || new Date().toISOString().substring(0, 10),
    });

    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [formData, setFormData] = useState({
        productId: '',
        userId: '',
        userName: '',
        type: 'purchase',
        quantity: '',
        pricingMode: 'none', // 'none', 'percentage', 'price'
        percentage: '',
        price: '',
        date: new Date().toISOString().substring(0, 10),
        note: ''
    });

    useEffect(() => {
        dispatch(fetchProducts());
        dispatch(fetchUsers());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchInventoryLogs());
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            toast.success('Record added successfully');
            setShowAddModal(false);
            setFormData({
                productId: '',
                userId: '',
                userName: '',
                type: 'purchase',
                quantity: '',
                pricingMode: 'none',
                percentage: '',
                price: '',
                date: new Date().toISOString().substring(0, 10),
                note: ''
            });
            setUserSearchTerm('');
            dispatch(clearInventoryMessages());
        }
        if (error) {
            toast.error(error);
            dispatch(clearInventoryMessages());
        }
    }, [success, error, dispatch]);

    const filteredUsers = users.filter(user => {
        const name = (user.personalInformation?.fullName || user.personalDetails?.name || "").toLowerCase();
        const mobile = user.personalInformation?.mobileNumber || user.personalDetails?.mobile || "";
        return name.includes(userSearchTerm.toLowerCase()) || mobile.includes(userSearchTerm);
    });

    const handleAddRecord = async (e) => {
        e.preventDefault();
        if (!formData.productId || !formData.quantity || !formData.date) {
            toast.error('Please fill all required fields');
            return;
        }

        const selectedProduct = products.find(p => p.id === formData.productId);

        dispatch(createInventoryRecord({
            ...formData,
            productName: selectedProduct?.name || 'Unknown Product',
            userName: formData.userName || userSearchTerm, // Fallback to typed text if no user selected
            quantity: Number(formData.quantity)
        }));
    };

    const handleDelete = (logId) => {
        if (window.confirm('Are you sure you want to delete this record? This will revert the product stock count.')) {
            dispatch(removeInventoryRecord(logId));
        }
    };

    const filteredLogs = logs.filter(log => {
        const matchesProduct = !filters.productId || log.productId === filters.productId;
        const matchesType = !filters.type || log.type === filters.type;
        const matchesDate = (!filters.fromDate || log.date >= filters.fromDate) &&
            (!filters.toDate || log.date <= filters.toDate);
        return matchesProduct && matchesType && matchesDate;
    });

    const calculateVPForLog = (log) => {
        const product = products.find(p => p.id === log.productId);
        const vp = Number(product?.volPoint || 0);
        return (vp * Number(log.quantity)).toFixed(2);
    };

    const handleExport = (format) => {
        const exportData = filteredLogs.map(log => ({
            Date: log.date,
            Product: log.productName,
            Beneficiary: log.userName || '-',
            Type: typeLabels[log.type]?.label || log.type,
            Quantity: log.quantity,
            Price: log.price || (log.percentage ? `${log.percentage}%` : '-'),
            VP: calculateVPForLog(log),
            Note: log.note || ''
        }));

        if (format === 'excel') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "InventoryHistory");
            XLSX.writeFile(workbook, `Inventory_History_${new Date().toISOString().split('T')[0]}.xlsx`);
        } else if (format === 'csv') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const csv = XLSX.utils.sheet_to_csv(worksheet);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute("download", `Inventory_History_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'pdf') {
            const doc = new jsPDF();
            doc.text("Inventory History Report", 14, 15);
            autoTable(doc, {
                head: [['Date', 'Product', 'Beneficiary', 'Type', 'Qty', 'Price/%', 'Note']],
                body: exportData.map(d => [d.Date, d.Product, d.Beneficiary, d.Type, d.Quantity, d.Price, d.Note]),
                startY: 20,
            });
            doc.save(`Inventory_History_${new Date().toISOString().split('T')[0]}.pdf`);
        }
        setShowExportMenu(false);
        toast.success(`Exporting as ${format.toUpperCase()}...`);
    };

    const typeLabels = {
        purchase: { label: 'Purchase (In)', color: 'text-green-600 bg-green-50' },
        clubUsed: { label: 'Club Used (Out)', color: 'text-blue-600 bg-blue-50' },
        sale: { label: 'Sale (Out)', color: 'text-purple-600 bg-purple-50' },
        personalUsed: { label: 'Personal (Out)', color: 'text-gray-600 bg-gray-50' },
        gifted: { label: 'Gifted (Out)', color: 'text-orange-600 bg-orange-50' }
    };

    return (
        <div className='min-h-screen bg-neutral-100 p-4 md:p-6 lg:p-8 font-sans text-neutral-800'>
            <div className='space-y-6'>
                {/* Header */}
                <div className='bg-white rounded-2xl border border-neutral-200/60 shadow-sm p-4 md:p-6 mb-4'>
                    <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
                        <div className='flex items-center gap-4'>
                            <button
                                onClick={() => navigate('/admin/products')}
                                className='p-2 hover:bg-neutral-100 rounded-xl transition-colors'
                            >
                                <ArrowLeft className='w-5 h-5' />
                            </button>
                            <div>
                                <h1 className='text-xl md:text-2xl font-bold text-neutral-900 tracking-tight'>
                                    Inventory History
                                </h1>
                                <p className='text-xs md:text-base text-neutral-500 mt-1'>
                                    Track and manage all product stock movements.
                                </p>
                            </div>
                        </div>
                        <div className='flex items-center gap-2'>
                            <div className='relative'>
                                <button
                                    onClick={() => setShowExportMenu(!showExportMenu)}
                                    className='h-11 px-6 bg-white border border-neutral-200 text-neutral-700 rounded-xl font-medium text-sm transition-all hover:bg-neutral-50 flex items-center gap-2'
                                >
                                    <FileDown className='w-4 h-4' />
                                    Export
                                    <ChevronDown className='w-4 h-4' />
                                </button>
                                {showExportMenu && (
                                    <div className='absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in duration-100'>
                                        <button
                                            onClick={() => handleExport('excel')}
                                            className='w-full flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors border-b border-neutral-100'
                                        >
                                            <FileSpreadsheet className='w-4 h-4 text-green-600' />
                                            Export to Excel
                                        </button>
                                        <button
                                            onClick={() => handleExport('csv')}
                                            className='w-full flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors border-b border-neutral-100'
                                        >
                                            <FileText className='w-4 h-4 text-blue-600' />
                                            Export to CSV
                                        </button>
                                        <button
                                            onClick={() => handleExport('pdf')}
                                            className='w-full flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors'
                                        >
                                            <Printer className='w-4 h-4 text-red-600' />
                                            Export as PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setShowAddModal(true)}
                                className='h-11 px-6 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl font-medium text-sm transition-all shadow-lg shadow-neutral-900/10 flex items-center gap-2'
                            >
                                <Plus className='w-4 h-4' />
                                Add Record
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <AdminCard title="Search & Filters">
                    <div className='p-4 md:p-5 grid grid-cols-1 md:grid-cols-3 gap-4'>
                        <div className='space-y-2'>
                            <label className='text-xs font-semibold text-neutral-500 uppercase tracking-wider'>Product</label>
                            <CustomDropdown
                                options={[
                                    { value: '', label: 'All Products' },
                                    ...products.map(p => ({ value: p.id, label: p.name }))
                                ]}
                                value={filters.productId}
                                onChange={(val) => setFilters(prev => ({ ...prev, productId: val }))}
                            />
                        </div>
                        <div className='space-y-2'>
                            <label className='text-xs font-semibold text-neutral-500 uppercase tracking-wider'>Transaction Type</label>
                            <CustomDropdown
                                options={[
                                    { value: '', label: 'All Types' },
                                    { value: 'purchase', label: 'Purchase' },
                                    { value: 'clubUsed', label: 'Club Used' },
                                    { value: 'sale', label: 'Sale' },
                                    { value: 'personalUsed', label: 'Personal Used' },
                                    { value: 'gifted', label: 'Gifted' }
                                ]}
                                value={filters.type}
                                onChange={(val) => setFilters(prev => ({ ...prev, type: val }))}
                            />
                        </div>
                        <div className='space-y-2'>
                            <label className='text-xs font-semibold text-neutral-500 uppercase tracking-wider'>From Date</label>
                            <div className='relative'>
                                <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400' />
                                <input
                                    type='date'
                                    value={filters.fromDate}
                                    onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))}
                                    className='w-full h-[42px] pl-10 pr-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-200 transition-all text-sm'
                                />
                            </div>
                        </div>
                        <div className='space-y-2'>
                            <label className='text-xs font-semibold text-neutral-500 uppercase tracking-wider'>To Date</label>
                            <div className='relative'>
                                <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400' />
                                <input
                                    type='date'
                                    value={filters.toDate}
                                    onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))}
                                    className='w-full h-[42px] pl-10 pr-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-200 transition-all text-sm'
                                />
                            </div>
                        </div>
                    </div>
                </AdminCard>

                {/* History Table */}
                <div className='bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden'>
                    <div className='overflow-x-auto'>
                        <table className='w-full text-left'>
                            <thead>
                                <tr className='bg-neutral-50 border-b border-neutral-200'>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Date</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Product</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Beneficiary</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Type</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Qty</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Price/%</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Total VP</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Note</th>
                                    <th className='px-6 py-4 text-xs font-bold text-neutral-500 uppercase tracking-wider'>Actions</th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-neutral-100'>
                                {loading ? (
                                    <tr>
                                        <td colSpan='6' className='px-6 py-12 text-center'>
                                            <div className='flex justify-center items-center gap-3 text-neutral-400'>
                                                <div className='w-5 h-5 border-2 border-neutral-300 border-t-neutral-600 rounded-full animate-spin' />
                                                Loading history...
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan='6' className='px-6 py-12 text-center text-neutral-400 text-sm'>
                                            No records found matching your filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLogs.map((log) => (
                                        <tr key={log.id} className='hover:bg-neutral-50 transition-colors'>
                                            <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-700'>
                                                {log.date}
                                            </td>
                                            <td className='px-6 py-4 text-sm text-neutral-900'>
                                                <div className='font-bold'>{log.productName}</div>
                                            </td>
                                            <td className='px-6 py-4 text-sm text-neutral-600 font-medium'>
                                                {log.userName || '-'}
                                            </td>
                                            <td className='px-6 py-4 whitespace-nowrap'>
                                                <span className={`px-2.5 py-1 text-[11px] font-bold rounded-full uppercase tracking-tight ${typeLabels[log.type]?.color || 'bg-gray-100'}`}>
                                                    {typeLabels[log.type]?.label || log.type}
                                                </span>
                                            </td>
                                            <td className='px-6 py-4 whitespace-nowrap text-sm font-bold text-neutral-900'>
                                                {log.quantity}
                                            </td>
                                            <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-600'>
                                                {log.price ? `₹${log.price}` : (log.percentage ? `${log.percentage}%` : '-')}
                                            </td>
                                            <td className='px-6 py-4 whitespace-nowrap text-sm font-bold text-neutral-900 bg-neutral-50/50'>
                                                {calculateVPForLog(log)}
                                            </td>
                                            <td className='px-6 py-4 text-sm text-neutral-500 italic max-w-xs truncate'>
                                                {log.note || '-'}
                                            </td>
                                            <td className='px-6 py-4 whitespace-nowrap'>
                                                <button
                                                    onClick={() => handleDelete(log.id)}
                                                    className='p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add Record Modal */}
            {showAddModal && (
                <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/60 backdrop-blur-sm'>
                    <div className='bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all animate-in fade-in zoom-in duration-200'>
                        <div className='px-6 py-5 border-b border-neutral-100 flex justify-between items-center'>
                            <h2 className='text-xl font-bold text-neutral-900'>Add Inventory Record</h2>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className='p-2 hover:bg-neutral-100 rounded-xl transition-colors'
                            >
                                <X className='w-5 h-5 text-neutral-400' />
                            </button>
                        </div>

                        <form onSubmit={handleAddRecord} className='p-6 space-y-4'>
                            <div className='space-y-2'>
                                <label className='text-sm font-semibold text-neutral-700'>Product <span className='text-red-500'>*</span></label>
                                <CustomDropdown
                                    options={products.map(p => ({ value: p.id, label: p.name }))}
                                    value={formData.productId}
                                    onChange={(val) => setFormData(prev => ({ ...prev, productId: val }))}
                                    placeholder="Select Product"
                                />
                            </div>

                            {/* User Selection */}
                            <div className='space-y-2 relative'>
                                <label className='text-sm font-semibold text-neutral-700'>Beneficiary Name (Select User or Type Name)</label>
                                <div className='relative'>
                                    <input
                                        type='text'
                                        value={userSearchTerm}
                                        onChange={(e) => {
                                            setUserSearchTerm(e.target.value);
                                            setShowUserDropdown(true);
                                            setFormData(prev => ({ ...prev, userName: e.target.value, userId: '' }));
                                        }}
                                        onFocus={() => setShowUserDropdown(true)}
                                        placeholder="Type name or search user..."
                                        className='w-full h-12 px-4 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-200 transition-all text-sm'
                                    />
                                    {showUserDropdown && userSearchTerm && (
                                        <div className='absolute z-[60] left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-neutral-200 rounded-xl shadow-xl'>
                                            {filteredUsers.length > 0 ? (
                                                filteredUsers.map(u => (
                                                    <button
                                                        key={u.id}
                                                        type='button'
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                userId: u.id,
                                                                userName: u.personalInformation?.fullName || u.personalDetails?.name
                                                            }));
                                                            setUserSearchTerm(u.personalInformation?.fullName || u.personalDetails?.name);
                                                            setShowUserDropdown(false);
                                                        }}
                                                        className='w-full px-4 py-2.5 text-left text-sm hover:bg-neutral-50 border-b border-neutral-50 last:border-0'
                                                    >
                                                        <div className='font-medium'>{u.personalInformation?.fullName || u.personalDetails?.name}</div>
                                                        <div className='text-[10px] text-neutral-500'>{u.personalInformation?.mobileNumber || u.personalDetails?.mobile}</div>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className='px-4 py-3 text-sm text-neutral-400 italic font-sans'>
                                                    No users found. Use typed name.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-2'>
                                    <label className='text-sm font-semibold text-neutral-700'>Type <span className='text-red-500'>*</span></label>
                                    <CustomDropdown
                                        options={[
                                            { value: 'purchase', label: 'Purchase (In)' },
                                            { value: 'clubUsed', label: 'Club Used (Out)' },
                                            { value: 'sale', label: 'Sale (Out)' },
                                            { value: 'personalUsed', label: 'Personal (Out)' },
                                            { value: 'gifted', label: 'Gifted (Out)' }
                                        ]}
                                        value={formData.type}
                                        onChange={(val) => setFormData(prev => ({ ...prev, type: val }))}
                                    />
                                </div>
                                <AdminInput
                                    label="Quantity"
                                    type="number"
                                    value={formData.quantity}
                                    onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                                    placeholder="Enter Qty"
                                    required
                                />
                            </div>

                            {/* Pricing Logic */}
                            <div className='p-4 bg-neutral-50 rounded-2xl space-y-3 border border-neutral-100'>
                                <label className='text-xs font-bold text-neutral-400 uppercase tracking-widest'>Pricing Details (Optional)</label>
                                <div className='flex gap-2'>
                                    {['none', 'percentage', 'price'].map((mode) => (
                                        <button
                                            key={mode}
                                            type='button'
                                            onClick={() => setFormData(prev => ({ ...prev, pricingMode: mode, percentage: '', price: '' }))}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold capitalize transition-all border ${formData.pricingMode === mode
                                                ? 'bg-neutral-900 text-white border-neutral-900'
                                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                                                }`}
                                        >
                                            {mode}
                                        </button>
                                    ))}
                                </div>

                                {formData.pricingMode === 'percentage' && (
                                    <AdminInput
                                        label="Percentage (%)"
                                        type="number"
                                        value={formData.percentage}
                                        onChange={(e) => setFormData(prev => ({ ...prev, percentage: e.target.value }))}
                                        placeholder="e.g. 25"
                                    />
                                )}

                                {formData.pricingMode === 'price' && (
                                    <AdminInput
                                        label="Amount (₹)"
                                        type="number"
                                        value={formData.price}
                                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                        placeholder="e.g. 500"
                                    />
                                )}
                            </div>

                            <div className='grid grid-cols-1 gap-4'>
                                <AdminInput
                                    label="Date"
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                    required
                                />
                            </div>

                            <AdminInput
                                label="Note (Optional)"
                                value={formData.note}
                                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                                placeholder="e.g., Purchased from stockist"
                            />

                            <div className='pt-4'>
                                <button
                                    type='submit'
                                    disabled={loading}
                                    className='w-full h-12 bg-neutral-900 text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-neutral-800 transition-all disabled:opacity-50'
                                >
                                    {loading ? (
                                        <div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                                    ) : (
                                        <>
                                            <CheckCircle2 className='w-5 h-5' />
                                            Save Record
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryHistory;
